import React, { useEffect } from "react";
import { X, Lightbulb, Target, Timer, Info } from "@phosphor-icons/react";

export const PRODUCT_INFO = {
  // ----- Commercial Real Estate -----
  "SBA 504": {
    blurb: "A government-backed loan program designed to help businesses buy the commercial real estate they operate in (or heavy equipment) with one of the lowest down payments available.",
    bestFor: "Owner-occupied buildings — you plan to run your business from at least 51% of the property.",
    terms: "~10% down, 10–25 year terms, below-market fixed rates on the SBA portion.",
    note: "Approval takes longer than conventional financing because the SBA is involved — plan for extra paperwork.",
  },
  "Conventional CRE": {
    blurb: "A standard commercial mortgage from a bank or private lender used to purchase or refinance income-producing or owner-occupied commercial property.",
    bestFor: "Stabilized properties and borrowers with solid credit and financials.",
    terms: "Typically 20–30% down, 5–30 year terms, competitive fixed or variable rates.",
    note: "The most common path — strong documentation gets you the best pricing.",
  },
  "Bridge": {
    blurb: "A short-term loan that 'bridges' the gap until you sell, stabilize, or refinance a property into permanent financing.",
    bestFor: "Time-sensitive purchases, value-add projects, or properties not yet ready for bank financing.",
    terms: "6–36 months, usually interest-only, faster closings, higher rates than permanent debt.",
    note: "Have a clear exit plan (sale or refinance) — that's what lenders underwrite to.",
  },
  "CMBS": {
    blurb: "A conduit loan that gets pooled with others and sold to bond investors. In exchange you get aggressive, non-recourse terms on stabilized income property.",
    bestFor: "Larger, stabilized income-producing properties ($2M+) where non-recourse matters.",
    terms: "Fixed 5–10 year terms, ~25–30 year amortization, non-recourse with standard carve-outs.",
    note: "Prepayment is restrictive (defeasance/yield maintenance) — best when you plan to hold.",
  },
  "Construction": {
    blurb: "Financing for ground-up builds or major renovations. Funds are released in stages (draws) as construction milestones are completed.",
    bestFor: "Developers and owners building new or substantially rehabbing a property.",
    terms: "Interest-only during the build, 12–36 months, converts or refinances at completion.",
    note: "Lenders will want a budget, timeline, and contractor details — have them ready.",
  },
  "Mezzanine": {
    blurb: "A second layer of financing that sits behind your first mortgage, letting you push total leverage higher without raising more equity.",
    bestFor: "Experienced sponsors filling the gap between senior debt and their equity on larger deals.",
    terms: "Higher rates than senior debt; secured by ownership interests rather than the property itself.",
    note: "Your senior lender must allow it — we coordinate the intercreditor details.",
  },
  "Hard Money": {
    blurb: "Fast, asset-based lending where the property's value matters more than your credit profile. Built for speed and deals banks can't do.",
    bestFor: "Tight closing timelines, credit challenges, or unconventional properties.",
    terms: "Days-to-weeks closings, 6–24 month terms, higher rates and points.",
    note: "A great tool to win the deal now and refinance into cheaper money later.",
  },
  "Fix & Flip": {
    blurb: "Short-term financing to buy a property, renovate it, and sell it for a profit. Covers the purchase plus a rehab budget released in draws.",
    bestFor: "Investors renovating properties for resale.",
    terms: "6–18 months, interest-only, funds purchase + rehab; repaid when the property sells.",
    note: "Lenders look at the after-repair value (ARV) — a solid scope of work strengthens your file.",
  },
  "Flip & Hold": {
    blurb: "Buy and renovate like a flip — but instead of selling, refinance into long-term rental financing and keep the property as an income asset.",
    bestFor: "Investors building a rental portfolio through the BRRRR strategy.",
    terms: "Short-term rehab financing up front, then a 30-year rental (DSCR) refinance at stabilization.",
    note: "The rental income needs to cover the long-term payment — we'll model the DSCR with you.",
  },
  // ----- Business -----
  "SBA 7(a)": {
    blurb: "The SBA's flagship government-guaranteed loan — the most flexible program for working capital, acquisitions, equipment, real estate, or refinancing debt.",
    bestFor: "Established small businesses that want the lowest payments and longest terms available.",
    terms: "Up to $5M, 10–25 year terms, competitive rates capped by the SBA.",
    note: "More documentation and a longer timeline than online lenders — worth it for the pricing.",
  },
  "Working Capital": {
    blurb: "Short-term funding for the day-to-day — payroll, inventory, marketing, or smoothing out a seasonal cash-flow dip.",
    bestFor: "Covering operating expenses or jumping on a short-term opportunity.",
    terms: "Fast approvals, 3–24 month terms, daily/weekly or monthly payments.",
    note: "Speed costs more than a bank term loan — match the term to how quickly the funds pay off.",
  },
  "Equipment": {
    blurb: "Financing to purchase machinery, vehicles, or technology where the equipment itself serves as the collateral.",
    bestFor: "Acquiring revenue-generating equipment while preserving cash.",
    terms: "Up to 100% financing, terms matched to the equipment's useful life (2–7 yrs).",
    note: "Potential tax advantages (Section 179) — ask your accountant about expensing the purchase.",
  },
  "Line of Credit": {
    blurb: "A revolving credit line you can draw from, repay, and draw again — and you only pay interest on what you actually use.",
    bestFor: "A flexible safety net for cash flow swings and unexpected expenses.",
    terms: "Revolving, interest only on drawn funds, renews periodically.",
    note: "Best opened before you need it — approval is easier when cash flow is strong.",
  },
  "Term Loan": {
    blurb: "A lump sum repaid in predictable installments over a fixed period. The classic business loan for a defined investment.",
    bestFor: "Expansion, renovations, acquisitions — any one-time investment with a clear payoff.",
    terms: "1–10 year terms, fixed or variable rates, predictable monthly payments.",
    note: "Stronger financials and time in business unlock larger amounts and better rates.",
  },
  "Merchant Cash Advance": {
    blurb: "An advance against your future sales, repaid automatically as a percentage of daily card receipts or revenue — payments flex with your sales.",
    bestFor: "Businesses with strong card/daily sales that need funds fast and may not qualify elsewhere.",
    terms: "Funding in as little as 24–48 hours, factor-rate pricing (no traditional interest rate or term).",
    note: "This is the fastest but typically most expensive option — best for short-term, high-return needs.",
  },
};

export const PRODUCT_GROUPS = {
  "Commercial Real Estate": [
    "SBA 504", "Conventional CRE", "Bridge", "CMBS", "Construction", "Mezzanine",
    "Hard Money", "Fix & Flip", "Flip & Hold",
  ],
  "Business Loans": [
    "SBA 7(a)", "Working Capital", "Equipment", "Line of Credit", "Term Loan", "Merchant Cash Advance",
  ],
};

export function ProductOverview({ product, compact = false }) {
  const info = PRODUCT_INFO[product];
  if (!info) return null;
  return (
    <div
      className={compact ? "p-4 mt-2" : "p-5 mt-3"}
      style={{ background: "var(--bg, #F5F7FB)", border: "1px solid var(--border, #D9E0EB)", borderRadius: 6 }}
      data-testid="product-overview-inline"
    >
      <p className="text-[10px] uppercase tracking-[3px] mb-1.5" style={{ color: "var(--muted-fg, #5A6478)" }}>
        About this product
      </p>
      <p className="text-sm leading-relaxed" style={{ color: "var(--ink, #2C3344)" }}>{info.blurb}</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3">
        <MiniRow Icon={Target} label="Best for" text={info.bestFor} />
        <MiniRow Icon={Timer} label="Typical structure" text={info.terms} />
        <MiniRow Icon={Lightbulb} label="Good to know" text={info.note} />
      </div>
    </div>
  );
}

function MiniRow({ Icon, label, text }) {
  return (
    <div className="flex items-start gap-2 p-2.5" style={{ background: "var(--surface, #fff)", borderRadius: 4 }}>
      <Icon size={14} weight="duotone" style={{ color: "var(--primary, #1F5DC6)", marginTop: 2, flexShrink: 0 }} />
      <div>
        <p className="text-[9px] uppercase tracking-widest" style={{ color: "var(--muted-fg, #5A6478)" }}>{label}</p>
        <p className="text-xs mt-0.5" style={{ color: "var(--ink, #2C3344)" }}>{text}</p>
      </div>
    </div>
  );
}

export default function ProductInfoModal({ product, onClose }) {
  const info = PRODUCT_INFO[product];
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!info) return null;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center p-4"
      style={{ background: "rgba(14, 21, 37, 0.55)", backdropFilter: "blur(3px)" }}
      onClick={onClose}
      data-testid="product-info-modal"
    >
      <div
        className="w-full max-w-lg p-7 relative"
        style={{ background: "var(--surface, #fff)", border: "1px solid var(--border, #D9E0EB)", borderRadius: 6, maxHeight: "85vh", overflowY: "auto" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="absolute top-4 right-4"
          onClick={onClose}
          aria-label="Close"
          style={{ color: "var(--muted-fg, #5A6478)" }}
          data-testid="product-info-close"
        >
          <X size={18} />
        </button>
        <p className="text-[10px] uppercase tracking-[3px] mb-2" style={{ color: "var(--muted-fg, #5A6478)" }}>
          Loan product guide
        </p>
        <h3 className="font-display text-2xl mb-3" style={{ color: "var(--ink, #0E1525)", fontWeight: 400 }} data-testid="product-info-title">
          {product}
        </h3>
        <p className="text-sm leading-relaxed" style={{ color: "var(--ink, #2C3344)" }}>{info.blurb}</p>

        <div className="mt-5 space-y-3">
          <InfoRow Icon={Target} label="Best for" text={info.bestFor} />
          <InfoRow Icon={Timer} label="Typical structure" text={info.terms} />
          <InfoRow Icon={Lightbulb} label="Good to know" text={info.note} />
        </div>

        <div className="flex items-center justify-between gap-3 mt-6 pt-4" style={{ borderTop: "1px solid var(--border, #D9E0EB)" }}>
          <p className="text-[11px] flex items-center gap-1.5" style={{ color: "var(--muted-fg, #5A6478)" }}>
            <Info size={13} /> Not sure? Pick your best guess — our team will confirm the right fit with you.
          </p>
          <button type="button" className="btn-primary shrink-0" onClick={onClose} data-testid="product-info-gotit">
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ Icon, label, text }) {
  return (
    <div className="flex items-start gap-3 p-3" style={{ background: "var(--bg, #F5F7FB)", borderRadius: 4 }}>
      <Icon size={16} weight="duotone" style={{ color: "var(--primary, #1F5DC6)", marginTop: 2, flexShrink: 0 }} />
      <div>
        <p className="text-[10px] uppercase tracking-widest" style={{ color: "var(--muted-fg, #5A6478)" }}>{label}</p>
        <p className="text-sm mt-0.5" style={{ color: "var(--ink, #2C3344)" }}>{text}</p>
      </div>
    </div>
  );
}
